import { yupResolver } from '@hookform/resolvers/yup';
import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useNavigate } from 'react-router-dom';

import { PermissionsEnum } from '@/auth/interfaces';
import {
  PERMANENCIAS_CHOICE,
  ROUTER_PATHS,
  TIPO_PLANES_CHOICE,
  TIPO_SERVICIOS_CHOICE,
  UNIDADES_VELOCIDAD_CHOICE,
  gridSizeMdLg12,
  gridSizeMdLg6,
  hasAllPermissions,
  newPlanSchema,
} from '@/shared';
import {
  CreatePlanParams,
  useCreatePlan,
  useUpdatePlan,
} from '@/shared/actions/erp';
import {
  CustomAutocompleteArrString,
  CustomNumberTextField,
  CustomTextField,
  SampleCheckbox,
  SingleFormBoxScene,
} from '@/shared/components/ui';
import { Plan } from '@/shared/interfaces';
import { Grid } from '@mui/material';

export type SavePlanProps = {
  titlePage: string;
  editPlan?: Plan;
};

type SaveFormData = CreatePlanParams & {};

const SavePlan: React.FC<SavePlanProps> = ({ titlePage, editPlan }) => {
  hasAllPermissions([
    PermissionsEnum.api_view_grupoipv4,
    PermissionsEnum.api_view_grupoipv6,
  ]);

  const returnUrlPlansPage = ROUTER_PATHS.servicioInternet.planesNav;

  const navigate = useNavigate();
  const [shrink, setShrink] = useState(false);

  ///* form ---------------------
  const form = useForm<SaveFormData>({
    resolver: yupResolver(newPlanSchema) as any,
    defaultValues: {
      es_discapacidad: false,
      es_tercera_edad: false,
      estado: true,
    },
  });
  const {
    handleSubmit,
    reset,
    formState: { errors, isValid },
  } = form;

  ///* fetch data ---------------------
  // const {
  //   data: ipv4GroupsPaging,
  //   isLoading: isLoadingIpv4Groups,
  //   isRefetching: isRefetchingIpv4Groups,
  // } = useFetchIpv4Groups({
  //   page_size: 510,
  // });
  // const {
  //   data: ipv6GroupsPaging,
  //   isLoading: isLoadingIpv6Groups,
  //   isRefetching: isRefetchingIpv6Groups,
  // } = useFetchIpv6Groups({
  //   page_size: 510,
  // });

  ///* mutations ---------------------
  const createPlanMutation = useCreatePlan({
    navigate,
    returnUrl: returnUrlPlansPage,
  });
  const updatePlanMutation = useUpdatePlan({
    navigate,
    returnUrl: returnUrlPlansPage,
  });

  ///* handlers ---------------------
  const onSave = (formData: SaveFormData) => {
    if (!isValid) return;

    ///* edit
    if (editPlan?.id_plan) {
      return updatePlanMutation.mutate({
        id: editPlan.id_plan,
        data: formData,
      });
    }

    ///* create
    return createPlanMutation.mutate(formData);
  };

  ///* effects ---------------------
  useEffect(() => {
    if (!editPlan?.id_plan) return;
    setShrink(true);
    reset({ ...editPlan, estado: !!editPlan.estado });
  }, []);

  // useLoaders(
  //   isLoadingIpv4Groups ||
  //     isRefetchingIpv4Groups ||
  //     isLoadingIpv6Groups ||
  //     isRefetchingIpv6Groups
  // );
  // if (
  //   editPlan &&
  //   (isLoadingIpv4Groups ||
  //     isRefetchingIpv4Groups ||
  //     isLoadingIpv6Groups ||
  //     isRefetchingIpv6Groups)
  // )
  //   return null;

  return (
    <SingleFormBoxScene
      titlePage={titlePage}
      onCancel={() => {
        navigate(returnUrlPlansPage);
      }}
      onSave={handleSubmit(onSave, err => {
        console.log(err);
      })}
    >

      <CustomTextField
        label="Nombre"
        name="nombre"
        control={form.control}
        defaultValue={form.getValues().nombre || ''}
        error={errors.nombre}
        helperText={errors.nombre?.message}
        shrink={shrink}
      />

      <CustomTextField
        label="Código"
        name="codigo"
        control={form.control}
        defaultValue={form.getValues().codigo || ''}
        error={errors.codigo}
        helperText={errors.codigo?.message ?? ''}
        shrink={shrink}
        size={gridSizeMdLg6}
        disabled={!!editPlan}
      />

      <CustomTextField
        label="Número de referencia"
        name="numero_referencia"
        control={form.control}
        defaultValue={form.getValues().numero_referencia || ''}
        error={errors.numero_referencia}
        helperText={errors.numero_referencia?.message ?? ''}
        shrink={shrink}
        size={gridSizeMdLg6}
        disabled={!!editPlan}
      />

      <CustomNumberTextField
        label="Velocidad de descarga máxima"
        name="velocidad_descarga_maxima"
        control={form.control}
        error={errors.velocidad_descarga_maxima}
        helperText={errors.velocidad_descarga_maxima?.message}
        shrink={shrink}
        min={0}
        size={gridSizeMdLg6}
      />
      <CustomNumberTextField
        label="Velocidad de descarga mínima"
        name="velocidad_descarga_minima"
        control={form.control}
        error={errors.velocidad_descarga_minima}
        helperText={errors.velocidad_descarga_minima?.message}
        shrink={shrink}
        min={0}
        size={gridSizeMdLg6}
      />
      <CustomNumberTextField
        label="Velocidad de subida máxima"
        name="velocidad_subida_maxima"
        control={form.control}
        error={errors.velocidad_subida_maxima}
        helperText={errors.velocidad_subida_maxima?.message}
        shrink={shrink}
        min={0}
        size={gridSizeMdLg6}
      />
      <CustomNumberTextField
        label="Velocidad de subida mínima"
        name="velocidad_subida_minima"
        control={form.control}
        error={errors.velocidad_subida_minima}
        helperText={errors.velocidad_subida_minima?.message}
        shrink={shrink}
        min={0}
        size={gridSizeMdLg6}
      />

      <CustomTextField
        label="Compartición"
        name="comparticion"
        control={form.control}
        defaultValue={form.getValues().comparticion || ''}
        error={errors.comparticion}
        helperText={errors.comparticion?.message}
        shrink={shrink}
        size={gridSizeMdLg6}
      />
      <CustomNumberTextField
        label="Valor del plan"
        name="valor"
        control={form.control}
        error={errors.valor}
        helperText={errors.valor?.message}
        shrink={shrink}
        min={0}
        size={gridSizeMdLg6}
      />

      {/* ------ fk ------- */}
      {/* <CustomAutocomplete<Ipv4Group>
        label="Grupo IPv4"
        name="grupo_ipv4"
        // options
        options={ipv4GroupsPaging?.data?.data || []}
        valueKey="nombre"
        actualValueKey="codigo"
        defaultValue={form.getValues().grupo_ipv4 || ''}
        optionLabelForEdit={form.getValues().grupo_ipv4}
        isLoadingData={isLoadingIpv4Groups}
        // validation
        control={form.control}
        error={errors.grupo_ipv4}
        helperText={errors.grupo_ipv4?.message}
        size={gridSizeMdLg6}
      />
      <CustomAutocomplete<Ipv6Group>
        label="Grupo IPv6"
        name="grupo_ipv6"
        // options
        options={ipv6GroupsPaging?.data?.data || []}
        valueKey="nombre"
        actualValueKey="codigo"
        defaultValue={form.getValues().grupo_ipv6 || ''}
        optionLabelForEdit={form.getValues().grupo_ipv6}
        isLoadingData={isLoadingIpv6Groups}
        // validation
        control={form.control}
        error={errors.grupo_ipv6}
        helperText={errors.grupo_ipv6?.message}
        size={gridSizeMdLg6}
      /> */}

      <CustomAutocompleteArrString
        label="Permanencia"
        name="permanencia"
        control={form.control}
        defaultValue={form.getValues('permanencia')}
        options={PERMANENCIAS_CHOICE}
        isLoadingData={false}
        error={errors.permanencia}
        helperText={errors.permanencia?.message}
        size={gridSizeMdLg6}
        disableClearable
      />
      <CustomAutocompleteArrString
        label="Tipo de servicio"
        name="tipo_servicio"
        control={form.control}
        defaultValue={form.getValues('tipo_servicio')}
        options={TIPO_SERVICIOS_CHOICE}
        isLoadingData={false}
        error={errors.tipo_servicio}
        helperText={errors.tipo_servicio?.message}
        size={gridSizeMdLg6}
        disableClearable
      />
      <CustomAutocompleteArrString
        label="Tipo de plan"
        name="tipo_plan"
        control={form.control}
        defaultValue={form.getValues('tipo_plan')}
        options={TIPO_PLANES_CHOICE}
        isLoadingData={false}
        error={errors.tipo_plan}
        helperText={errors.tipo_plan?.message}
        size={gridSizeMdLg6}
        disableClearable
      />
      <CustomAutocompleteArrString
        label="Unidad"
        name="unidad"
        control={form.control}
        defaultValue={form.getValues('unidad')}
        options={UNIDADES_VELOCIDAD_CHOICE}
        isLoadingData={false}
        error={errors.unidad}
        helperText={errors.unidad?.message}
        size={gridSizeMdLg6}
        disableClearable
      />


      <CustomTextField
        label="Line Profile"
        name="line_profile"
        control={form.control}
        defaultValue={form.getValues().line_profile || ''}
        error={errors.line_profile}
        helperText={errors.line_profile?.message}
        shrink={shrink}
        size={gridSizeMdLg6}
        required={false}
      />
      <CustomTextField
        label="Traffic Tables"
        name="traffic_table"
        control={form.control}
        defaultValue={form.getValues().traffic_table || ''}
        error={errors.traffic_table}
        helperText={errors.traffic_table?.message}
        shrink={shrink}
        size={gridSizeMdLg6}
        required={false}
      />
      {/* <CustomTextField
        label="VLAN de Servicio"
        name="vlan_servicio"
        control={form.control}
        defaultValue={form.getValues().vlan_servicio || ''}
        error={errors.vlan_servicio}
        helperText={errors.vlan_servicio?.message}
        shrink={shrink}
        size={gridSizeMdLg6}
        required={false}
      /> */}
      <CustomTextField
        label="GEMPort"
        name="gemport"
        control={form.control}
        defaultValue={form.getValues().gemport || ''}
        error={errors.gemport}
        helperText={errors.gemport?.message}
        shrink={shrink}
        size={gridSizeMdLg12}
        required={false}
      />



      <Grid item container spacing={3}>

        <SampleCheckbox
          label="Aplica para discapacidad"
          name="es_discapacidad"
          control={form.control}
          defaultValue={!!form.getValues().es_discapacidad}
          size={gridSizeMdLg6}
        />
        <SampleCheckbox
          label="Aplica para tercera edad"
          name="es_tercera_edad"
          control={form.control}
          defaultValue={!!form.getValues().es_tercera_edad}
          size={gridSizeMdLg6}
        />

        <SampleCheckbox
          label="Aplica bono desarrollo humano"
          name="es_bono_humano"
          control={form.control}
          defaultValue={!!form.getValues().es_bono_humano}
          size={gridSizeMdLg6}
        />

        <SampleCheckbox
          label="Aplica ayuda social"
          name="es_ayuda_social"
          control={form.control}
          defaultValue={!!form.getValues().es_ayuda_social}
          size={gridSizeMdLg6}
        />

        <SampleCheckbox
          label="Aplica Gremio"
          name="es_gremio"
          control={form.control}
          defaultValue={!!form.getValues().es_gremio}
          size={gridSizeMdLg6}
        />

        <SampleCheckbox
          label="Aplica RRHH"
          name="es_rrhh"
          control={form.control}
          defaultValue={!!form.getValues().es_rrhh}
          size={gridSizeMdLg6}
        />

        <SampleCheckbox
          label="Estado"
          name="estado"
          control={form.control}
          defaultValue={!!form.getValues().estado}
          isState
          size={gridSizeMdLg6}
        />
      </Grid>

    </SingleFormBoxScene>
  );
};

export default SavePlan;
